// Finish the Dialogue: a quiz master reads famous lines with a word missing; teams shout the answer.
import { useCallback, useState } from 'react';
import { trackEvent } from '../../lib/analytics';
import { awardLine, finishQuiz, newQuiz, originalAnswers, revealLine, skipLine, withMoreCards, type QuizState } from '@shared/minigames';
import { splitPrompt } from '@shared/rules';
import type { CardData } from '@shared/types';
import { useDocumentTitle } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import { canSpeak, speakCard } from '../../lib/speak';
import { gameInfo } from './info';
import {
  Choice,
  Countdown,
  StatsStatus,
  GameFrame,
  GameOverPanel,
  TeamEditor,
  TeamScores,
  teamNamesOrHints,
  useDealer,
  useStats,
  useTopUp,
  useNow,
  useStoredState,
  useTimerEffects,
  useWakeLock,
} from './kit';
import { buzzer, ding, primeAudio, swoosh, tick } from './sound';

const info = gameInfo('dialogue');

interface QuizSetup {
  teams: string[];
  lineSeconds: number;
  lines: number;
}

const DEFAULT_SETUP: QuizSetup = { teams: ['', ''], lineSeconds: 30, lines: 10 };
const TEAM_HINTS = ['Filmy Chachas', 'Gen Z', 'Mamas', 'Masis'];

function isQuiz(v: unknown): boolean {
  const s = v as QuizState | null;
  return s === null || (s?.kind === 'quiz' && Array.isArray(s.teams) && Array.isArray(s.history));
}

/** The line with its blank, or with the real words filled in once revealed. */
function Line({ card, revealed }: { card: CardData; revealed: boolean }) {
  const answers = originalAnswers(card);
  const parts = splitPrompt({ text: card.text, pick: answers.length > 1 ? answers.length : 1 }, revealed ? answers : undefined);
  return (
    <span className="mg-big mg-line">
      {parts.map((p, i) =>
        p.kind === 'text' ? (
          <span key={i}>{p.value}</span>
        ) : p.kind === 'fill' ? (
          <mark key={i} className="mg-fill pop-in">
            {p.value}
          </mark>
        ) : (
          <span key={i} className="blank" aria-label="blank" />
        ),
      )}
    </span>
  );
}

export function Dialogue() {
  useDocumentTitle(info.name);
  const stats = useStats('dialogue');
  const dealer = useDealer('dialogue');
  const { showGloss, showDevanagari, toast } = usePrefs();
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useStoredState<QuizSetup>('cm:mg:dialogue:setup', DEFAULT_SETUP, (v) => Array.isArray((v as QuizSetup)?.teams));
  const [state, setState] = useStoredState<QuizState | null>('cm:mg:dialogue:game', null, isQuiz);
  const byId = dealer.byId;
  const topUp = useCallback((ids: string[]) => setState((s) => (s ? withMoreCards(s, ids) : s)), [setState]);
  useTopUp(state, dealer, topUp);
  const total = stats.status === 'ready' ? stats.stats.total : 0;
  const timing = state?.phase === 'asking' && Boolean(state.endsAt);
  const now = useNow(timing);
  useWakeLock(Boolean(state && state.phase !== 'over'));

  const onEnd = useCallback(() => {
    buzzer();
    setState((s) => (s ? revealLine(s) : s));
  }, [setState]);
  useTimerEffects(timing ? state!.endsAt : null, now, onEnd, tick);

  const start = async (teams: string[]) => {
    primeAudio();
    setBusy(true);
    try {
      const dealt = await dealer.start({});
      setState(newQuiz(teams, { lineSeconds: setup.lineSeconds, lines: setup.lines }, dealt.ids, Date.now(), Math.random, dealt.total));
      trackEvent('minigame_started', { game: 'dialogue' });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not start the quiz.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const menu = state ? (
    <>
      {state.phase !== 'over' && (
        <button type="button" onClick={() => window.confirm('End the quiz and show the scores?') && setState((s) => (s ? finishQuiz(s) : s))}>
          🏁 End the quiz
        </button>
      )}
      <button type="button" className="danger" onClick={() => window.confirm('Quit this quiz and go back to setup?') && setState(null)}>
        ↩ Back to setup
      </button>
    </>
  ) : undefined;

  if (!state && stats.status !== 'ready') {
    return (
      <GameFrame title={info.name}>
        <main className="game-main mg-main">
          <StatsStatus stats={stats} />
        </main>
      </GameFrame>
    );
  }

  if (!state) {
    return (
      <GameFrame title={info.name}>
        <main className="game-main mg-main">
          <section className="mg-intro">
            <span className="mg-emoji" aria-hidden="true">
              {info.emoji}
            </span>
            <h1>{info.name}</h1>
            <p className="muted">{info.tagline}</p>
          </section>
          <details className="panel mg-rules">
            <summary>How to play</summary>
            <ol>
              {info.rules.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ol>
          </details>
          <section className="panel stack">
            <h2 style={{ margin: 0 }}>Teams</h2>
            <TeamEditor names={setup.teams} placeholders={TEAM_HINTS} onChange={(teams) => setSetup({ ...setup, teams })} />
          </section>
          <section className="panel stack">
            <h2 style={{ margin: 0 }}>Quiz</h2>
            <Choice
              label="Time to shout the answer"
              value={setup.lineSeconds}
              options={[
                [0, 'No timer'],
                [15, '15 sec'],
                [30, '30 sec'],
                [45, '45 sec'],
              ]}
              onChange={(lineSeconds) => setSetup({ ...setup, lineSeconds })}
            />
            <Choice
              label="Lines"
              value={setup.lines}
              options={[10, 15, 20, 30].map((n) => [n, String(n)])}
              onChange={(lines) => setSetup({ ...setup, lines })}
            />
            <p className="muted small" style={{ margin: 0 }}>
              {total} famous lines from films and songs, with translations.
            </p>
          </section>
        </main>
        <div className="action-bar">
          <div className="inner">
            <button type="button" className="btn btn-primary btn-lg btn-block" disabled={!total || busy} onClick={() => start(teamNamesOrHints(setup.teams, TEAM_HINTS))}>
              {busy ? 'Shuffling...' : 'Start the quiz'}
            </button>
          </div>
        </div>
      </GameFrame>
    );
  }

  if (state.phase === 'over') {
    return (
      <GameFrame title={info.name} menu={menu}>
        <GameOverPanel teams={state.teams} onAgain={() => start(state.teams.map((t) => t.name))} onSetup={() => setState(null)}>
          {state.history.length > 0 && (
            <details className="panel mg-rules">
              <summary>All the lines</summary>
              <ul className="mg-results">
                {state.history.map((h, i) => {
                  const card = byId.get(h.cardId);
                  return (
                    <li key={i}>
                      <span className="text">{card ? <Line card={card} revealed /> : 'A line'}</span>
                      <span className="chip">{h.team === null ? 'Nobody' : state.teams[h.team]?.name}</span>
                    </li>
                  );
                })}
              </ul>
            </details>
          )}
        </GameOverPanel>
      </GameFrame>
    );
  }

  const card = state.cardId ? byId.get(state.cardId) : undefined;
  const revealed = state.phase === 'revealed';
  return (
    <GameFrame title={info.name} menu={menu}>
      <main className="game-main mg-main mg-playing">
        <TeamScores teams={state.teams} />
        <div className="row between" style={{ flexWrap: 'nowrap' }}>
          <span className="chip blue">
            Line {state.played + 1} of {state.settings.lines}
          </span>
          {canSpeak() && card && (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => speakCard(card)}>
              🔊 Read it out
            </button>
          )}
        </div>
        {state.endsAt && !revealed && <Countdown endsAt={state.endsAt} now={now} total={state.settings.lineSeconds} />}
        {card ? (
          <div className={`mg-card dialogue ${revealed ? 'revealed' : ''}`}>
            <Line card={card} revealed={revealed} />
            {showDevanagari && card.devanagari && (
              <span className="mg-gloss deva" lang="hi">
                {card.devanagari}
              </span>
            )}
            {showGloss && card.gloss && <span className="mg-gloss">{card.gloss}</span>}
            {revealed && card.explanation && <span className="mg-explain">{card.explanation}</span>}
          </div>
        ) : (
          <p className="muted center">This line has gone missing. Skip it.</p>
        )}
      </main>
      <div className="action-bar mg-actions">
        <div className="inner">
          {!revealed ? (
            <>
              <button
                type="button"
                className="btn btn-outline btn-lg"
                onClick={() => {
                  swoosh();
                  setState((s) => (s ? skipLine(s, Date.now()) : s));
                }}
              >
                Skip
              </button>
              <button type="button" className="btn btn-primary btn-lg grow" onClick={() => setState((s) => (s ? revealLine(s) : s))}>
                Reveal the answer
              </button>
            </>
          ) : (
            <div className="mg-award">
              <span className="small muted">Who got it first?</span>
              <div className="mg-award-buttons">
                {state.teams.map((t, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`btn btn-lg mg-team-btn t${i}`}
                    onClick={() => {
                      ding();
                      setState((s) => (s ? awardLine(s, i, Date.now()) : s));
                    }}
                  >
                    +1 {t.name}
                  </button>
                ))}
                <button type="button" className="btn btn-outline btn-lg" onClick={() => setState((s) => (s ? awardLine(s, null, Date.now()) : s))}>
                  Nobody
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </GameFrame>
  );
}
